import { useEffect, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { BracketInfo, Game, Prediction } from "../../types/custom_types";
import Navbar from "./navbar";
import Footer from "./footer";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";

const ROUND_LABELS: Record<number, string> = {
  1: "First Round",
  2: "Second Round",
  3: "Sweet 16",
  4: "Elite 8",
  5: "Final Four",
  6: "Championship",
};

const BracketCompare = () => {
  const theme = useTheme();
  const [brackets, setBrackets] = useState<BracketInfo[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  // Map<bracketId, Prediction[]>
  const [allPredictions, setAllPredictions] = useState<Map<number, Prediction[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [activeRound, setActiveRound] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      const [bracketsRes, gamesRes] = await Promise.all([
        axios.get<BracketInfo[]>(`${process.env.REACT_APP_API_URL}/userapi/brackets/`),
        axios.get<Game[]>(`${process.env.REACT_APP_API_URL}/userapi/games/`),
      ]);

      const fetchedBrackets = bracketsRes.data;
      const fetchedGames = gamesRes.data;

      const predictionResults = await Promise.all(
        fetchedBrackets.map((b) =>
          axios.get<Prediction[]>(
            `${process.env.REACT_APP_API_URL}/userapi/brackets/${b.id}/predictions/`
          )
        )
      );

      const predMap = new Map<number, Prediction[]>();
      fetchedBrackets.forEach((b, i) => {
        predMap.set(b.id, predictionResults[i].data);
      });

      setBrackets(fetchedBrackets);
      setGames(fetchedGames);
      setAllPredictions(predMap);
      setLoading(false);
    };

    fetchData();
  }, []);

  // Build lookup: gameId -> bracketId -> Prediction
  const predLookup = new Map<number, Map<number, Prediction>>();
  allPredictions.forEach((preds, bracketId) => {
    preds.forEach((pred) => {
      if (!predLookup.has(pred.game_id)) predLookup.set(pred.game_id, new Map());
      predLookup.get(pred.game_id)!.set(bracketId, pred);
    });
  });

  const gamesInRound = games.filter((g) => g.round === activeRound);

  const getCellColor = (pred: Prediction | undefined, game: Game | undefined): string | undefined => {
    if (!pred || !game?.winner_name) return undefined;
    return pred.predicted_winner_name === game.winner_name
      ? theme.palette.success.main
      : theme.palette.error.main;
  };

  const getCellTextColor = (pred: Prediction | undefined, game: Game | undefined): string | undefined => {
    if (!pred || !game?.winner_name) return undefined;
    return theme.palette.common.white;
  };

  const gameLabel = (game: Game): string => {
    if (game.team1_name && game.team2_name) {
      if (game.round === 1) {
        const s1 = game.team1_seed ? `(${game.team1_seed}) ` : "";
        const s2 = game.team2_seed ? `(${game.team2_seed}) ` : "";
        return `${s1}${game.team1_name} vs ${s2}${game.team2_name}`;
      }
      return `${game.team1_name} vs ${game.team2_name}`;
    }
    return "TBD vs TBD";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: theme.gradients.contentBg,
      }}
    >
      <Navbar />

      <Container maxWidth={false} sx={{ py: 4, flex: 1, px: { xs: 1, md: 3 } }}>
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography
            variant="h5"
            sx={{ fontFamily: theme.fonts.display, color: "primary.main", mb: 0.5 }}
          >
            Bracket Comparison
          </Typography>
          <Typography variant="body2" color="text.secondary">
            See what every bracket picked for each game. Green = correct, Red = wrong.
          </Typography>
        </Paper>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && brackets.length === 0 && (
          <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">No brackets have been created yet.</Typography>
          </Paper>
        )}

        {!loading && brackets.length > 0 && (
          <Paper elevation={2}>
            <Tabs
              value={activeRound}
              onChange={(_, v) => setActiveRound(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}
            >
              {Object.entries(ROUND_LABELS).map(([round, label]) => (
                <Tab key={round} value={Number(round)} label={label} />
              ))}
            </Tabs>

            <TableContainer sx={{ maxHeight: "70vh", overflowX: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        minWidth: 200,
                        fontWeight: 700,
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        background: theme.palette.background.paper,
                        borderRight: `2px solid ${theme.palette.divider}`,
                      }}
                    >
                      Matchup
                    </TableCell>
                    {brackets.map((b, i) => (
                      <TableCell
                        key={b.id}
                        align="center"
                        sx={{
                          minWidth: 130,
                          fontWeight: 700,
                          background:
                            i === 0
                              ? theme.palette.secondary.main
                              : theme.palette.background.paper,
                          color:
                            i === 0
                              ? theme.palette.secondary.contrastText
                              : "inherit",
                        }}
                      >
                        <Box
                          component={RouterLink}
                          to={`/bracket/${b.id}`}
                          sx={{
                            textDecoration: "none",
                            color: "inherit",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="caption" fontWeight={700}>
                            #{i + 1} {b.name}
                          </Typography>
                          <Chip
                            label={`${b.score} pts`}
                            size="small"
                            sx={{
                              fontSize: "0.65rem",
                              height: 18,
                              background: i === 0 ? "rgba(255,255,255,0.25)" : undefined,
                              color: i === 0 ? theme.palette.secondary.contrastText : undefined,
                            }}
                          />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gamesInRound.map((game) => (
                    <TableRow key={game.id} hover>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          background: theme.palette.background.paper,
                          borderRight: `2px solid ${theme.palette.divider}`,
                          fontSize: "0.75rem",
                        }}
                      >
                        {gameLabel(game)}
                      </TableCell>
                      {brackets.map((b) => {
                        const pred = predLookup.get(game.id)?.get(b.id);
                        const bgColor = getCellColor(pred, game);
                        const textColor = getCellTextColor(pred, game);
                        return (
                          <TableCell
                            key={b.id}
                            align="center"
                            sx={{
                              fontSize: "0.72rem",
                              backgroundColor: bgColor,
                              color: textColor,
                              fontWeight: bgColor ? 600 : undefined,
                            }}
                          >
                            {pred?.predicted_winner_name ?? "—"}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  {gamesInRound.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={brackets.length + 1}
                        align="center"
                        sx={{ py: 3, color: "text.secondary" }}
                      >
                        No games in this round yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>

      <Footer />
    </Box>
  );
};

export default BracketCompare;
